import { useEffect, useMemo, useRef, useState } from 'react';
import { useLingui } from '@lingui/react';
import { Loader2, User, UserMinus, Users } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { EmployeeSelector } from '@/components/form/EmployeeSelector';
import { useAuthContext } from '@/contexts/AuthContext';
import { useEmployeePicker } from '@/hooks/useEmployees';
import { useIsAdmin } from '@/hooks/useIsAdmin';
import { useAssignWorkflowItem } from '@/hooks/useWorkflows';
import type { WorkflowItemResponse } from '@/types/workflow';
import { workflowItemPermissions } from '@/types/workflowResponsible';
import { returnFocusOnClose } from './returnFocus';

/** The slice of an item the dialog needs. */
export type AssignDialogItem = Pick<
  WorkflowItemResponse,
  'id' | 'category' | 'admin_only' | 'assigned_to' | 'group_lead_employee_id' | 'group_lead_name' | 'group_lead_clerk_user_id' | 'group_name'
>;

interface AssignDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  item: AssignDialogItem | null;
  onSuccess?: () => void;
  /** Gets the focus back when the dialog closes: the element that opened it. */
  returnFocusTo?: HTMLElement | null;
}

/**
 * Hand a workflow item to a person. An admin or the item's group lead gets the
 * full dialog; anyone else may only take the item or hand it to the group lead
 * (and remove themselves). Offers the two shortcuts that cover most
 * cases (me, the group lead of the child's group), the full employee picker
 * for everyone else, and "nobody" to put the item back to its role queue.
 */
export function AssignDialog({ open, onOpenChange, item, onSuccess, returnFocusTo }: AssignDialogProps) {
  const { i18n } = useLingui();
  const { userId } = useAuthContext();
  const isAdmin = useIsAdmin();
  const picker = useEmployeePicker();
  const assign = useAssignWorkflowItem();

  // The employee record of the signed-in user, for "Take it myself".
  const me = useMemo(
    () => (picker.data ?? []).find((employee) => !!userId && employee.clerkUserId === userId),
    [picker.data, userId],
  );
  // The current holder's employee id, to preselect it.
  const current = useMemo(
    () => (picker.data ?? []).find((employee) => !!item?.assigned_to && employee.clerkUserId === item.assigned_to),
    [picker.data, item?.assigned_to],
  );

  const [employeeId, setEmployeeId] = useState<string | undefined>(undefined);
  const [comment, setComment] = useState('');

  // Reset only when the dialog opens. The picker may resolve after that, and a
  // dependency on the holder would then wipe what was typed or picked; the ref
  // hands the effect the latest holder without making it a trigger.
  const currentRef = useRef(current);
  currentRef.current = current;
  useEffect(() => {
    if (open) {
      setEmployeeId(currentRef.current?.id);
      setComment('');
    }
  }, [open]);
  // A holder that arrives after opening still preselects, unless the user
  // already picked someone.
  useEffect(() => {
    if (open && current?.id) {
      setEmployeeId((picked) => picked ?? current.id);
    }
  }, [open, current?.id]);

  if (!item) return null;

  // Only admins see an admin-only item, so its group lead is no hand-over target.
  const adminOnly = !!item.admin_only;
  const leadCanTake = !adminOnly && !!item.group_lead_employee_id && !!item.group_lead_clerk_user_id;
  const unchanged = (employeeId ?? '') === (current?.id ?? '');
  const permissions = workflowItemPermissions(item, userId, isAdmin);
  const canRemove = !!item.assigned_to && permissions.canRemove;

  const submit = (targetEmployeeId: string) => {
    assign.mutate(
      { id: item.id ?? '', employeeId: targetEmployeeId, comment: comment.trim() || undefined },
      {
        onSuccess: () => {
          onOpenChange(false);
          onSuccess?.();
        },
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="assign-dialog" onCloseAutoFocus={returnFocusOnClose(returnFocusTo)}>
        <DialogHeader>
          <DialogTitle>{i18n._({ id: 'workflow.assign.title', message: 'Who takes this?' })}</DialogTitle>
          <DialogDescription>
            {i18n._({
              id: 'workflow.assign.description',
              message: 'It goes to the person you pick. The team still sees it.',
            })}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="flex flex-wrap gap-2">
            {me ? (
              <Button
                type="button"
                size="sm"
                variant={employeeId === me.id ? 'default' : 'outline'}
                onClick={() => setEmployeeId(me.id)}
                data-testid="assign-me"
              >
                <User className="mr-1 h-3.5 w-3.5" aria-hidden="true" />
                {i18n._({ id: 'workflow.assign.me', message: 'Me' })}
              </Button>
            ) : (
              <>
                <Button type="button" size="sm" variant="outline" disabled data-testid="assign-me">
                  <User className="mr-1 h-3.5 w-3.5" aria-hidden="true" />
                  {i18n._({ id: 'workflow.assign.me', message: 'Me' })}
                </Button>
                {/* Blame the login only when the picker answered for a signed-in user:
                    a loading or failed picker has no rows to match. */}
                {picker.isError ? (
                  <span className="self-center text-xs text-destructive" role="alert" data-testid="assign-picker-error">
                    {i18n._({ id: 'workflow.assign.pickerError', message: 'Could not load the staff list' })}
                  </span>
                ) : picker.isSuccess && !!userId ? (
                  <span className="self-center text-xs text-muted-foreground" data-testid="assign-me-reason">
                    {i18n._({
                      id: 'workflow.assign.meNoEmployee',
                      message: 'Your login is not linked to an employee',
                    })}
                  </span>
                ) : null}
              </>
            )}
            {leadCanTake && (
              <Button
                type="button"
                size="sm"
                variant={employeeId === item.group_lead_employee_id ? 'default' : 'outline'}
                onClick={() => setEmployeeId(item.group_lead_employee_id)}
                data-testid="assign-lead"
                title={item.group_name ?? undefined}
              >
                <Users className="mr-1 h-3.5 w-3.5" aria-hidden="true" />
                {i18n._({
                  id: 'workflow.assign.lead',
                  message: 'Group lead: {name}',
                  values: { name: item.group_lead_name ?? '' },
                })}
              </Button>
            )}
            {!adminOnly && item.group_lead_employee_id && !item.group_lead_clerk_user_id && (
              <span className="self-center text-xs text-muted-foreground" data-testid="assign-lead-no-login">
                {i18n._({
                  id: 'workflow.assign.leadNoLogin',
                  message: '{name} leads the group but has no login yet.',
                  values: { name: item.group_lead_name ?? '' },
                })}
              </span>
            )}
          </div>

          {permissions.canHandToAnyone && (
          <div className="space-y-1.5">
            <Label htmlFor="assign-employee">
              {i18n._({ id: 'workflow.assign.employee', message: 'Someone else' })}
            </Label>
            <EmployeeSelector
              id="assign-employee"
              value={employeeId}
              onChange={setEmployeeId}
              requireLogin
              placeholder={i18n._({ id: 'workflow.assign.search', message: 'Search by name...' })}
            />
          </div>
          )}

          <div className="space-y-1.5">
            <Label htmlFor="assign-comment">
              {i18n._({ id: 'workflow.assign.comment', message: 'Note (optional)' })}
            </Label>
            <Textarea
              id="assign-comment"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              rows={2}
              maxLength={10000}
              placeholder={i18n._({ id: 'workflow.assign.commentPlaceholder', message: 'Why this person, or what to look at' })}
            />
          </div>
        </div>

        <DialogFooter className="gap-2 sm:justify-between">
          {canRemove ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="text-muted-foreground"
              disabled={assign.isPending}
              onClick={() => submit('')}
              data-testid="assign-remove"
            >
              <UserMinus className="mr-1 h-3.5 w-3.5" aria-hidden="true" />
              {i18n._({ id: 'workflow.assign.remove', message: 'Remove person' })}
            </Button>
          ) : (
            <span />
          )}
          <div className="flex gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={assign.isPending}>
              {i18n._({ id: 'common.cancel', message: 'Cancel' })}
            </Button>
            <Button
              type="button"
              disabled={!employeeId || unchanged || assign.isPending}
              onClick={() => employeeId && submit(employeeId)}
              data-testid="assign-confirm"
            >
              {assign.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />}
              {i18n._({ id: 'workflow.assign.confirm', message: 'Hand over' })}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
